import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import withConfig from 'config/withConfig';
import IconTimes from '@spothero/icons/times';
import Button from '@spothero/ui/button';
import StorageUtils from '@spothero/utils/storage';
import SearchUtils from 'search/search-utils';
import SearchAirportMap from 'search/map/SearchAirportMap';
import SpotListNoResults from 'search/spot-list/SpotListNoResults';
import SearchAirportSort from './SearchAirportSort';
import SearchAirportCard from './SearchAirportCard';
import SHLoader from 'common/loader/SHLoader';
import {getSpots} from 'spots/spots-selectors';
import {Helmet} from 'react-helmet-async';
import SHUtils from 'utils/sh-utils';

class SearchAirportResults extends Component {
    static propTypes = {
        iataCode: PropTypes.string,
        isLoading: PropTypes.bool,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        spotCount: PropTypes.number,
        starts: PropTypes.string,
        ends: PropTypes.string,
        siteUrl: PropTypes.string.isRequired,
        citySlug: PropTypes.string.isRequired,
        destinationRelativeUrl: PropTypes.string.isRequired,
        urlParams: PropTypes.string.isRequired,
        sortBy: PropTypes.string.isRequired,
    };

    state = {
        isMapView: false,
    };

    _onToggleViewType = () => {
        this.setState(state => ({isMapView: !state.isMapView}));
    };

    _onSpotSelected = rank => {
        const {sortBy} = this.props;

        StorageUtils.set(SearchUtils.SEGMENT_SPOT_META, {rank, sortBy});
    };

    render() {
        const {
            iataCode,
            isLoading,
            starts,
            ends,
            spots,
            spotCount,
            siteUrl,
            citySlug,
            destinationRelativeUrl,
            urlParams,
        } = this.props;
        const {isMapView} = this.state;
        const listViewClasses = classNames('SearchAirportResults-pane', {
            'SearchAirportResults-pane-inactive': isMapView,
        });
        const mapViewClasses = classNames(
            'SearchAirportResults-pane',
            {'SearchAirportResults-pane-inactive': !isMapView || isLoading},
            'SearchAirportResults-faux-modal-sticky-panel'
        );
        const bodyClasses = classNames(
            SHUtils.determineInitialLoadBodyClasses({
                isSearchPage: true,
                isAirport: true,
            }),
            {
                'search-airport-page-inactive': isMapView,
            }
        );

        return (
            <div className="SearchAirportResults">
                <Helmet>
                    <body className={bodyClasses} />
                </Helmet>
                <section className={listViewClasses}>
                    {Boolean(spotCount) && (
                        <SearchAirportSort
                            spotCount={spotCount}
                            onToggleViewType={this._onToggleViewType}
                        />
                    )}
                    <ul
                        className="SearchAirportResults-spots"
                        itemScope
                        itemType="http://schema.org/ItemList"
                    >
                        <link
                            itemProp="url"
                            href={`${siteUrl}/${citySlug}/${destinationRelativeUrl}${urlParams}`}
                        />
                        {Boolean(spotCount) && (
                            <meta
                                itemProp="numberOfItems"
                                content={spotCount}
                            />
                        )}
                        {isLoading ? (
                            <SHLoader />
                        ) : spotCount ? (
                            spots.map((spot, index) => {
                                const rank = index + 1;

                                return (
                                    <li
                                        key={spot.spotId}
                                        itemProp="itemListElement"
                                        itemScope
                                        itemType="http://schema.org/ListItem"
                                    >
                                        <meta
                                            itemProp="position"
                                            content={rank}
                                        />
                                        <SearchAirportCard
                                            spot={spot}
                                            starts={starts}
                                            ends={ends}
                                            rank={rank}
                                            onClick={this._onSpotSelected}
                                        />
                                    </li>
                                );
                            })
                        ) : (
                            <li>
                                <SpotListNoResults isAirport />
                            </li>
                        )}
                    </ul>
                </section>
                <section className={mapViewClasses}>
                    <header className="SearchAirportResults-map-header">
                        <h1>{`Parking${
                            iataCode ? ` Near ${iataCode}` : ''
                        }`}</h1>
                        <Button onClick={this._onToggleViewType}>
                            <IconTimes />
                        </Button>
                    </header>
                    <div className="SearchAirportResults-map-container">
                        <SearchAirportMap
                            spots={spots}
                            onMarkerClick={this._onSpotSelected}
                        />
                    </div>
                </section>
            </div>
        );
    }
}

const makeMapStateToProps = () => state => {
    const {
        city: {
            data: {slug: citySlug},
        },
        destination: {
            data: {
                airport: {iata_code: iataCode},
                relative_url: destinationRelativeUrl,
            },
        },
        spots: {isPending, airportPendingRequests},
        search: {
            data: {sortBy},
        },
        searchRequest: {starts, ends},
    } = state;
    const spots = getSpots(state);

    return {
        iataCode,
        starts,
        ends,
        spots,
        citySlug,
        destinationRelativeUrl,
        urlParams: `?starts=${encodeURIComponent(
            starts
        )}&ends=${encodeURIComponent(ends)}`,
        spotCount: spots ? spots.length : 0,
        sortBy,
        // isPending is the default state, before any requests have been made
        // airportPendingRequests is an array, so we can allow multiple pending requests without running into a race condition
        isLoading: isPending || Boolean(airportPendingRequests.length),
    };
};
const enhance = compose(
    withConfig(['siteUrl']),
    connect(makeMapStateToProps)
);

export default enhance(SearchAirportResults);
